/**
 * SupabaseOnboardingStore — IN-3.b
 * ────────────────────────────────
 * Real Supabase-backed implementation of the OnboardingBackend interface.
 *
 * Interface constraint: loadSnapshot() and saveSnapshot() are synchronous
 * (required by OnboardingProvider's useRef initialization). We bridge this
 * gap with an in-memory cache:
 *
 *   • loadSnapshot()  — returns the cached snapshot synchronously (empty on
 *                       first call). Call hydrateAsync() inside a useEffect
 *                       after mount to populate the cache from Supabase and
 *                       trigger a state re-hydration in the provider.
 *   • saveSnapshot()  — synchronously updates the in-memory cache, then fires
 *                       async upserts to Supabase as a background operation.
 *                       Errors are logged but do not throw (matching local
 *                       store behavior for QuotaExceededError).
 *
 * The OnboardingProvider must call hydrateAsync() once on mount (useEffect)
 * and re-initialize state from the result. See the usage note below.
 *
 * organization_id comes from getCurrentOrganizationId() (auth.helpers).
 *
 * Tables (see migrations/20260522_onboarding_tables.sql):
 *   onboarding_invite_tokens
 *   onboarding_sessions
 *   onboarding_audit_events
 *
 * complianceDocuments are hydrated from installer_compliance_documents via
 * contractor_crews so contractor portal uploads surface in admin compliance.
 */

import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type {
  OnboardingAgreementSignature,
  OnboardingAuditEvent,
  OnboardingInviteToken,
  OnboardingSession,
} from "../types";
import type {
  ComplianceDocument,
  ComplianceDocumentType,
} from "@/data/contractor/compliance/complianceDocuments";
import {
  listInstallerComplianceReviewDocuments,
  type InstallerComplianceReviewDocument,
} from "./installerComplianceReview";
import {
  emptyOnboardingSnapshot,
  ONBOARDING_SNAPSHOT_VERSION,
  type OnboardingBackend,
  type OnboardingSnapshot,
} from "./types";

// ── DB row → TS type helpers ──────────────────────────────────────────────────

function readAgreementSignatures(value: unknown): OnboardingAgreementSignature[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is OnboardingAgreementSignature => {
    if (!item || typeof item !== "object") return false;
    const record = item as Record<string, unknown>;
    return (
      typeof record.id === "string" &&
      typeof record.variant === "string" &&
      typeof record.version === "string" &&
      typeof record.signedAt === "string" &&
      typeof record.signedBy === "string"
    );
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToToken(row: Record<string, any>): OnboardingInviteToken {
  return {
    id: row.id,
    token: row.token,
    contractorId: row.contractor_id ?? undefined,
    parentId: row.parent_id ?? undefined,
    entityType: row.entity_type,
    createdBy: row.created_by,
    createdAt: row.created_at,
    expiresAt: row.expires_at,
    status: row.status,
    consumedAt: row.consumed_at ?? undefined,
    consumedBy: row.consumed_by ?? undefined,
    revokedAt: row.revoked_at ?? undefined,
    revokedBy: row.revoked_by ?? undefined,
    revokedReason: row.revoked_reason ?? undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToSession(row: Record<string, any>): OnboardingSession {
  const draft = row.draft ?? {};
  const columnSignatures = readAgreementSignatures(row.signatures);
  const draftSignatures = readAgreementSignatures(draft.signatures);

  return {
    id: row.id,
    organizationId: row.organization_id ?? undefined,
    contractorId: row.contractor_id ?? undefined,
    tokenId: row.token_id,
    entityType: row.entity_type,
    parentEntityId: row.parent_entity_id ?? undefined,
    currentStep: row.current_step,
    completedSteps: Array.isArray(row.completed_steps) ? row.completed_steps : [],
    status: row.status,
    draft,
    signatures: columnSignatures.length > 0 ? columnSignatures : draftSignatures,
    payoutMethod: row.payout_method ?? draft.payoutMethod ?? undefined,
    startedAt: row.started_at,
    lastTouchedAt: row.last_touched_at,
    submittedAt: row.submitted_at ?? undefined,
    reuploadRequestedSteps: Array.isArray(row.reupload_requested_steps)
      ? row.reupload_requested_steps
      : undefined,
    reuploadReason: row.reupload_reason ?? undefined,
    approvedAt: row.approved_at ?? undefined,
    approvedBy: row.approved_by ?? undefined,
    approvalReason: row.approval_reason ?? undefined,
    rejectedAt: row.rejected_at ?? undefined,
    rejectedBy: row.rejected_by ?? undefined,
    rejectionReason: row.rejection_reason ?? undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToAuditEvent(row: Record<string, any>): OnboardingAuditEvent {
  return {
    id: row.id,
    at: row.at,
    actor: row.actor,
    action: row.action,
    tokenId: row.token_id ?? undefined,
    sessionId: row.session_id ?? undefined,
    documentId: row.document_id ?? undefined,
    reason: row.reason ?? undefined,
    metadata: row.metadata ?? undefined,
  };
}

function normalizeWebsiteDocumentType(raw: unknown): ComplianceDocumentType {
  const compact = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const map: Record<string, ComplianceDocumentType> = {
    background_check: "background_check",
    background: "background_check",
    bg_check: "background_check",
    driver_license: "license",
    drivers_license: "license",
    license: "license",
    insurance: "insurance",
    coi: "insurance",
    w9: "w9",
    w_9: "w9",
    form_w9: "w9",
    agreement: "agreement",
    contract: "agreement",
    banking_verification: "banking_verification",
    bank: "banking_verification",
    osha_10: "certification",
    osha10: "certification",
    manufacturer_cert: "certification",
    certification: "certification",
  };
  return map[compact] ?? "other";
}

function rowToComplianceDocument(
  row: InstallerComplianceReviewDocument,
): ComplianceDocument {
  const fileName =
    row.file_name ??
    undefined;
  const storagePath =
    row.storage_path ?? undefined;

  return {
    id: String(row.id),
    contractorId: row.contractor_id,
    type: normalizeWebsiteDocumentType(row.type),
    url: storagePath ? String(storagePath) : undefined,
    fileName: fileName ? String(fileName) : undefined,
    uploadedAt: String(row.uploaded_at ?? row.created_at ?? new Date().toISOString()),
    uploadedBy: String(row.installer_user_id ?? "installer"),
    expiresAt: row.expires_at ? String(row.expires_at) : undefined,
    verifiedAt: row.verified_at ? String(row.verified_at) : undefined,
    verifiedBy: row.verified_by ? String(row.verified_by) : undefined,
    rejectedAt: row.rejected_at ? String(row.rejected_at) : undefined,
    rejectedBy: row.rejected_by ? String(row.rejected_by) : undefined,
    rejectionReason: row.rejection_reason ? String(row.rejection_reason) : undefined,
    supersededBy: row.superseded_by ? String(row.superseded_by) : undefined,
    metadata: {
      source: "installer_compliance_documents",
      organizationId: row.organization_id ?? null,
      installerUserId: row.installer_user_id ?? null,
      contractorCrewId: row.contractor_crew_id ?? null,
      contractorCrewStatus: row.contractor_crew_status ?? null,
      contractorCrewRole: row.contractor_crew_role ?? null,
      installerCrewName: row.installer_crew_name ?? row.installer_full_name ?? row.installer_email ?? null,
      installerFullName: row.installer_full_name ?? null,
      installerEmail: row.installer_email ?? null,
      contractorCompanyName: row.contractor_company_name ?? null,
      rawType: row.type ?? null,
      mimeType: row.mime_type ?? null,
      fileSize: row.byte_size ?? null,
      storageBucket: "compliance-documents",
      storagePath: storagePath ?? null,
      supersedesId: row.supersedes_id ?? null,
    },
  };
}

function buildProfileLabel(row: {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}): { fullName: string | null; email: string | null } {
  const composedName = [
    String(row.first_name ?? "").trim(),
    String(row.last_name ?? "").trim(),
  ].filter(Boolean).join(" ");

  return {
    fullName: String(row.full_name ?? "").trim() || composedName || null,
    email: String(row.email ?? "").trim() || null,
  };
}

async function enrichComplianceRowsWithProfileLabels(
  rows: InstallerComplianceReviewDocument[],
): Promise<InstallerComplianceReviewDocument[]> {
  const missingLabelIds = Array.from(new Set(
    rows
      .filter((row) => !row.installer_crew_name && !row.installer_full_name && !row.installer_email)
      .map((row) => String(row.installer_user_id ?? "").trim())
      .filter(Boolean),
  ));
  if (missingLabelIds.length === 0) return rows;

  const [byProfile, byAuth] = await Promise.all([
    supabase
      .from("profiles")
      .select("id, auth_user_id, full_name, first_name, last_name, email")
      .in("id", missingLabelIds),
    supabase
      .from("profiles")
      .select("id, auth_user_id, full_name, first_name, last_name, email")
      .in("auth_user_id", missingLabelIds),
  ]);

  if (byProfile.error || byAuth.error) {
    console.warn("SupabaseOnboardingStore.enrichComplianceRowsWithProfileLabels:", byProfile.error ?? byAuth.error);
    return rows;
  }

  const labels = new Map<string, { fullName: string | null; email: string | null }>();
  for (const row of [
    ...((byProfile.data ?? []) as Array<{ id?: string; auth_user_id?: string | null; full_name?: string | null; first_name?: string | null; last_name?: string | null; email?: string | null }>),
    ...((byAuth.data ?? []) as Array<{ id?: string; auth_user_id?: string | null; full_name?: string | null; first_name?: string | null; last_name?: string | null; email?: string | null }>),
  ]) {
    const label = buildProfileLabel(row);
    const profileId = String(row.id ?? "").trim();
    const authUserId = String(row.auth_user_id ?? "").trim();
    if (profileId) labels.set(profileId, label);
    if (authUserId) labels.set(authUserId, label);
  }

  if (labels.size === 0) return rows;

  return rows.map((row) => {
    if (row.installer_crew_name || row.installer_full_name || row.installer_email) return row;

    const installerUserId = String(row.installer_user_id ?? "").trim();
    const label = labels.get(installerUserId);
    if (!label?.fullName && !label?.email) return row;

    return {
      ...row,
      installer_crew_name: label.fullName ?? label.email,
      installer_full_name: label.fullName,
      installer_email: label.email,
    };
  });
}

// ── TS type → DB row helpers ──────────────────────────────────────────────────

function tokenToRow(
  t: OnboardingInviteToken,
  organizationId: string,
): Record<string, unknown> {
  return {
    id: t.id,
    token: t.token,
    organization_id: organizationId,
    contractor_id: t.contractorId ?? null,
    parent_id: t.parentId ?? null,
    entity_type: t.entityType,
    created_by: t.createdBy,
    created_at: t.createdAt,
    expires_at: t.expiresAt,
    status: t.status,
    consumed_at: t.consumedAt ?? null,
    consumed_by: t.consumedBy ?? null,
    revoked_at: t.revokedAt ?? null,
    revoked_by: t.revokedBy ?? null,
    revoked_reason: t.revokedReason ?? null,
  };
}

function sessionToRow(
  s: OnboardingSession,
  organizationId: string,
): Record<string, unknown> {
  return {
    id: s.id,
    organization_id: organizationId,
    contractor_id: s.contractorId ?? null,
    token_id: s.tokenId,
    entity_type: s.entityType,
    parent_entity_id: s.parentEntityId ?? null,
    current_step: s.currentStep,
    completed_steps: s.completedSteps,
    status: s.status,
    draft: s.draft,
    signatures: s.signatures ?? [],
    payout_method: s.payoutMethod ?? null,
    started_at: s.startedAt,
    last_touched_at: s.lastTouchedAt,
    submitted_at: s.submittedAt ?? null,
    reupload_requested_steps: s.reuploadRequestedSteps ?? null,
    reupload_reason: s.reuploadReason ?? null,
    approved_at: s.approvedAt ?? null,
    approved_by: s.approvedBy ?? null,
    approval_reason: s.approvalReason ?? null,
    rejected_at: s.rejectedAt ?? null,
    rejected_by: s.rejectedBy ?? null,
    rejection_reason: s.rejectionReason ?? null,
  };
}

function auditEventToRow(
  e: OnboardingAuditEvent,
  organizationId: string,
): Record<string, unknown> {
  return {
    id: e.id,
    organization_id: organizationId,
    at: e.at,
    actor: e.actor,
    action: e.action,
    token_id: e.tokenId ?? null,
    session_id: e.sessionId ?? null,
    document_id: e.documentId ?? null,
    reason: e.reason ?? null,
    metadata: e.metadata ?? null,
  };
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const SUPABASE_NOT_CONNECTED_MESSAGE =
  "SupabaseOnboardingStore: Supabase is not connected. " +
  "Connect your Supabase project via Lovable's Supabase button, then set " +
  "VITE_ONBOARDING_BACKEND=supabase. Implementation lands in IN-3.b.";

export class SupabaseOnboardingStore implements OnboardingBackend {
  readonly kind = "supabase" as const;

  /** In-memory cache — populated by hydrateAsync() and kept in sync by saveSnapshot(). */
  private cache: OnboardingSnapshot = emptyOnboardingSnapshot();

  /**
   * Synchronous load — returns the current in-memory cache.
   *
   * On first call (before hydrateAsync() resolves) this returns an empty
   * snapshot. OnboardingProvider calls hydrateAsync() in a useEffect and
   * re-initialises state once the data arrives.
   */
  loadSnapshot(): OnboardingSnapshot {
    return this.cache;
  }

  /**
   * Synchronous save — updates the in-memory cache immediately, then fires
   * async upserts to Supabase in the background.
   *
   * Provider receives no error from this call (mirrors LocalOnboardingStore
   * behavior). Supabase errors are logged to the console.
   */
  saveSnapshot(snapshot: OnboardingSnapshot): void {
    this.cache = snapshot;
    // Fire-and-forget async persistence.
    void this._persistAsync(snapshot);
  }

  /**
   * Async hydration — must be called once from a useEffect in
   * OnboardingProvider after mount. Loads the full snapshot from Supabase,
   * updates the cache, and returns the snapshot so the provider can
   * re-initialise its React state.
   *
   * Returns an empty snapshot on any error so the provider never crashes.
   */
  async hydrateAsync(): Promise<OnboardingSnapshot> {
    try {
      const organizationId = await getCurrentOrganizationId();

      const [tokensResult, sessionsResult, auditResult, documents] = await Promise.all([
        supabase
          .from("onboarding_invite_tokens")
          .select("*")
          .eq("organization_id", organizationId),
        supabase
          .from("onboarding_sessions")
          .select("*")
          .eq("organization_id", organizationId),
        supabase
          .from("onboarding_audit_events")
          .select("*")
          .eq("organization_id", organizationId)
          .order("at", { ascending: true }),
        this._hydrateComplianceDocuments(organizationId),
      ]);

      if (tokensResult.error) {
        console.error("SupabaseOnboardingStore.hydrateAsync tokens:", tokensResult.error);
      }
      if (sessionsResult.error) {
        console.error("SupabaseOnboardingStore.hydrateAsync sessions:", sessionsResult.error);
      }
      if (auditResult.error) {
        console.error("SupabaseOnboardingStore.hydrateAsync audit:", auditResult.error);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const inviteTokens = (tokensResult.data ?? []).map((r: any) => rowToToken(r));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sessions = (sessionsResult.data ?? []).map((r: any) => rowToSession(r));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const auditEvents = (auditResult.data ?? []).map((r: any) => rowToAuditEvent(r));

      const snapshot: OnboardingSnapshot = {
        v: ONBOARDING_SNAPSHOT_VERSION,
        seq: this.cache.seq,
        inviteTokens,
        sessions,
        auditEvents,
        complianceDocuments: documents,
      };

      this.cache = snapshot;
      return snapshot;
    } catch (err) {
      console.error("SupabaseOnboardingStore.hydrateAsync:", err);
      return emptyOnboardingSnapshot();
    }
  }

  private async _hydrateComplianceDocuments(
    organizationId: string,
  ): Promise<ComplianceDocument[]> {
    try {
      const rows = await listInstallerComplianceReviewDocuments(organizationId);
      const enrichedRows = await enrichComplianceRowsWithProfileLabels(rows);
      return enrichedRows.map(rowToComplianceDocument);
    } catch (err) {
      console.error("SupabaseOnboardingStore._hydrateComplianceDocuments:", err);
      return [];
    }
  }

  /** Background async persistence. Errors are swallowed after logging. */
  private async _persistAsync(snapshot: OnboardingSnapshot): Promise<void> {
    try {
      const organizationId = await getCurrentOrganizationId();

      // Tokens — upsert all
      if (snapshot.inviteTokens.length > 0) {
        const rows = snapshot.inviteTokens.map((t) => tokenToRow(t, organizationId));
        const { error } = await supabase
          .from("onboarding_invite_tokens")
          .upsert(rows, { onConflict: "id" });
        if (error) {
          console.error("SupabaseOnboardingStore._persistAsync tokens:", error);
        }
      }

      // Sessions — upsert all
      if (snapshot.sessions.length > 0) {
        const rows = snapshot.sessions.map((s) => sessionToRow(s, organizationId));
        const { error } = await supabase
          .from("onboarding_sessions")
          .upsert(rows, { onConflict: "id" });
        if (error) {
          console.error("SupabaseOnboardingStore._persistAsync sessions:", error);
        }
      }

      // Audit events — insert only (append-only; upsert with onConflict=id is
      // a no-op for existing rows, which is the correct behavior for an audit log).
      if (snapshot.auditEvents.length > 0) {
        const rows = snapshot.auditEvents.map((e) => auditEventToRow(e, organizationId));
        const { error } = await supabase
          .from("onboarding_audit_events")
          .upsert(rows, { onConflict: "id", ignoreDuplicates: true });
        if (error) {
          console.error("SupabaseOnboardingStore._persistAsync audit:", error);
        }
      }
    } catch (err) {
      console.error("SupabaseOnboardingStore._persistAsync:", err);
    }
  }
}
